import camelcaseKeys from 'camelcase-keys'
import { create } from 'zustand'
import { supabase } from '../supabaseClient'
import { callTool } from '../lib/tools'
import {
  REFILL_REREAD_MS,
  creditPositionFrom,
  creditPurchasesFrom,
  rulePermitsRefill,
} from '../utilities/credit'
import { lowestComputeRate } from '../utilities/planLines'
import { ORG_BILLING_COLUMNS, readsMoney, subscriptionStatusFrom } from '../utilities/moneyReads'
import useOrganizations from './useOrganizations'
import { currentPeriod } from './useUsage'

// An edge-triggered billing alert row (2.10). The router writes them deduped per
// (org, period, kind, axis); the app reads them for the in-app banner.
export type BillingAlert = {
  kind: string // allowance_80 | allowance_100 | cap_80 | cap_100 | payment_failed | lapsed | restored
  // Which allowance the alert is about: bandwidth | storage | nodes | compute for allowance_*,
  // '' otherwise. ⚠ FOUR axes, not three — `compute` joined with router#933, and this comment
  // said three while BillingBanner named none of them, which is how "You've hit your free plan
  // limit" came to be a banner that could not say WHICH limit. Treat an unrecognised axis as
  // nameable-by-nobody and fall back to generic copy: the router owns this vocabulary and can
  // add to it without an app deploy.
  axis: string
  period: string
}

// A plan from the catalog (read straight from `plans` — readable by every authenticated user).
// camelCased on read; only the fields the billing screen renders are typed.
export type PlanRow = {
  id: string
  name: string
  monthlyFeeCents: number // flat plan base (seats are free — no per-seat price)
  bandwidthRateCentsPerGb: number
  // ⚠ OPTIONAL BECAUSE THE COLUMNS ARE BEING DROPPED. The one-meter model retires the storage
  // and node meters (nobgp-router#929): migration 0106 zeroes these rates and 0108 removes the
  // columns outright. The read is `select('*')`, so after 0108 the keys are simply ABSENT and
  // these arrive `undefined` — typing them `number` would have been a lie the compiler enforces
  // nowhere and `formatCents(undefined)` downstream. Every use must be guarded.
  storageRateCentsPerGbMonth?: number
  nodeOverageCents?: number
  // Whether the plan is still sold. Added by migration 0106; soft-retiring a plan (`business`)
  // is what hides it from the picker, and the router's `create_checkout` refuses one that is
  // not offered. ⚠ OPTIONAL, and read as `!== false`: before 0106 applies the key is absent,
  // and an absent flag must mean "offered" or the picker empties itself.
  offered?: boolean
  nodeLimit: number // included active nodes (hard cap on Free, allowance on paid)
  bandwidthBytesFree: number // included bandwidth
  storageBytesFree: number // included storage
  // ⚠ CENTS, NOT HOURS. One dollar pool is shared by every machine class, so no number of hours
  // names what is left (see `usageAxis.ts`). The card quotes it in dollars through `computeLine`
  // (app#331); nothing else may re-state the included figure.
  computeAllowanceCents: number
  metered: boolean
  hardCaps: boolean
  /**
   * Whether this plan may run a provisioned machine with NO deadline
   * (`max_hours: 0`). Added by router migration 0152; set for Pro and Business,
   * and for the exempt Enterprise tier from `0161` (router#1216). Free is false —
   * the deadline and the included hours are its only two bounds.
   *
   * ⚠ The app reads the SAME COLUMN the router decides on rather than keeping a
   * second list of which plans may. A second list is how a plan added later
   * silently gets the wrong answer on one of the two surfaces.
   *
   * ⚠ OPTIONAL, and read as `=== true`: `select('*')` returns whatever the row
   * has, and before `0152` applies the key is simply absent. Absent must mean
   * "not offered" here — the safe direction, since the only cost is one option
   * missing from a menu, where the other way round is a control whose only
   * outcome is a refusal.
   */
  computeDeadlineOptional?: boolean
  /**
   * The deadline in HOURS the router gives a machine when the caller names no `max_hours`
   * (router migration 0152; 24 on every plan today). NULL means no default. The deadline menus
   * mark this entry "(default)" and the dialog selects it first.
   * ⚠ OPTIONAL for the same reason as `computeDeadlineOptional`: `select('*')`.
   */
  computeMaxHoursDefault?: number | null
  /**
   * The longest deadline in HOURS a caller may ask for (router migration 0198, router#1549): 168
   * on Free, NULL (no maximum) on the plans that allow No time limit. The deadline menus grey out
   * the choices above it.
   * ⚠ OPTIONAL: before 0198 applies the key is absent, and absent greys nothing. That is safe in
   * either deploy order: the menus offer at most 7 days, and the router is what refuses.
   */
  computeMaxHoursLimit?: number | null
}

// The two plan predicates the throttle copy and the SSO upsell key on. Both derive from the plan's
// `hard_caps` / `metered` FLAGS rather than its name, so a renamed or newly-added hard-capped plan
// reads the same on every surface — Usage keyed on `planName === 'free'` and so disagreed with the
// banner (app#127).
//
// ⚠⚠ A NULL PLAN IS A THIRD STATE AND EVERY CONSUMER HAS TO SPELL IT (app#141). Both predicates
// return false for it, so `isFree ? … : …` does not "default to safe" — it silently routes the
// unknown case into whichever branch happens to be the else. BillingBanner's else was the PAID
// sentence, so a Free org read "metered overage now applies" and lost its Upgrade button.
// ⚠ It is NOT only the load window. `plan` and `alerts` are set together, so nothing renders
// while billing is loading; the state that PERSISTS is `organizations.plan_id` matching no row
// this reader can see in `plans`. Write the three cases out.
export const isFreePlan = (plan: PlanRow | null) => !!plan?.hardCaps
export const isPaidPlan = (plan: PlanRow | null) => !!plan && plan.metered && !plan.hardCaps

// The card the customer will actually be charged on (router#1133's `billing` tool).
//
// ⚠ Read live from Stripe on every call and never stored anywhere — here, or in the router, or in
// Postgres. The customer can change the card in the portal, and a copy would be a wrong statement
// about their money made silently.
// ⚠⚠ THE NAME IS HISTORICAL: IT IS NOT ALWAYS A CARD (router#1171). Stripe Link saves a method of
// type `link` that is charged every month and has no brand, no last four and no expiry — the card
// sits behind the Link account. `type` is always set by a current router; `account` names a method
// that a brand and last four cannot. Render it with `describePaymentMethod`, never by reading
// `brand` and `last4` directly.
//
// ⚠ `type` and `account` are absent from a router older than that fix, which is the deploy window.
// Both are typed as required because `camelcaseKeys` on an older payload leaves them undefined and
// the helper's first branch — a card, which HAS a brand — answers before either is read.
export type BillingCard = {
  type: string
  brand: string
  last4: string
  expMonth: number
  expYear: number
  account: string
}

// One row of the billing history.
export type BillingInvoice = {
  id: string
  number: string
  created: number // Unix seconds
  totalCents: number
  currency: string
  status: string
  hostedUrl: string
  pdf: string
}

// An organization's credit position, from the router's `credit` tool (router#1083, released in
// v0.4.114; `credit_billed` in v0.4.118).
//
// ⚠⚠ `creditBilled` AND `creditEnabled` ARE TWO QUESTIONS AND NEITHER IS THE WHOLE ANSWER.
// `creditBilled` is whether THIS DEPLOYMENT pays usage from a balance at all, or invoices the
// same usage in arrears; `creditEnabled` is the ORGANIZATION's own choice about spending its
// balance, and its default is TRUE. On prod today the first is false and the second is true, so
// a surface keyed on `creditEnabled` alone would render a live-looking balance that nothing
// spends and a Buy button that always fails. Render on `creditBilled`; ask `creditEnabled` only
// to describe the switch.
//
// ⚠ `balanceCents` is NULLABLE and null is not zero: the exempt (Enterprise) tier does not draw
// on a balance at all, and reporting 0 there says the opposite of what is true.
/**
 * One credit purchase, as the customer's own record of a card charge (nobgp-router#1209).
 *
 * ⚠⚠ THE ONLY IN-PRODUCT RECORD THERE IS. A credit purchase is a bare PaymentIntent and
 * deliberately not an invoice, so it appears on no invoice list; before this it appeared nowhere
 * at all. After the Stripe switch it is a real line on a card statement with nothing in the
 * product to reconcile it against.
 *
 * ⚠ `failure` is the router's MAPPED sentence and never the stored text. The column holds
 * engineering prose — measured on prod: "router#1197: the reservation committed and
 * BeginCreditPurchase could not read its id …" — and a Stripe error marshals its whole object,
 * including a link into our own dashboard. Rendering the column would be app#200 in a new place.
 */
export type CreditPurchase = {
  id: string
  amountCents: number
  kind: string // "manual" | "auto_reload"
  status: string // "pending" | "succeeded" | "failed"
  createdAt: string
  settledAt: string | null
  failure: string | null
}

export type CreditPosition = {
  orgId: string
  period: string
  periodEnd: string
  balanceCents: number | null
  autoReloadTriggerCents: number | null
  autoReloadReloadCents: number | null
  spentThisPeriodCents: number
  purchasedThisPeriodCents: number
  // ⚠⚠ WHAT THE CAP IS ACTUALLY MEASURED AGAINST (nobgp-router#1195), and NOT the field above:
  // that one counts manual purchases too, and computing refill headroom from it would put a
  // manual purchase back inside the cap by the back door. ⚠ Optional because a router that
  // predates #1195 does not send it — the deploy window, router first then this.
  autoRefilledThisPeriodCents?: number
  // ⚠⚠ THE PENDING FIGURE THE RESIDUAL WAS STANDING IN FOR (router#1206, app#201). It is money
  // bought this period whose charge has not settled — automatic and manual together, the same
  // scope as `purchasedThisPeriodCents`, of which it is the part that has not landed. ⚠ It is
  // ALREADY INSIDE both that total and, for the automatic part, `autoRefilledThisPeriodCents`;
  // adding it to either double-counts.
  //
  // ⚠ Optional for the deploy window — router first, then this — exactly like the field above.
  // `pendingCents` falls back to the residual while it is absent.
  pendingPurchaseCents?: number
  // When the OLDEST unsettled purchase was started, RFC 3339 UTC, or null when nothing is pending.
  // ⚠⚠ THE HALF THE RESIDUAL COULD NEVER CARRY. Arithmetic over three totals can say money is
  // missing; it cannot say since when, and "minutes" and "six hours" are different situations with
  // different actions. Optional for the deploy window; null is the ordinary "nothing pending".
  oldestPendingPurchaseAt?: string | null
  spendCapCents: number | null
  creditEnabled: boolean
  creditBilled: boolean
}

// One live grant behind the balance (get_org_credit, router migration 0138).
//
// ⚠⚠ THE PARTS BEHAVE DIFFERENTLY AND A SINGLE TOTAL HIDES IT, which is why app#146 asks for the
// breakdown: credit we GRANTED (a promotion or a service credit) EXPIRES, credit the customer
// BOUGHT does not, and the expiring part is always spent FIRST. A customer looking at one number
// cannot tell how much of it is about to lapse.
//
// ⚠ The rows arrive in `consume_usage`'s own spend order (axis, then expires_at nulls last,
// granted_at, id) and must be RENDERED in that order — it is the order they will actually be
// spent in, and re-sorting by size or kind would show a plausible sequence that is not the real
// one.
//
// ⚠ The RPC carries no `reason` and must not grow one here: that column is operator text and
// router#1057 is explicit that it is never rendered anywhere a tenant controls.
export type CreditGrant = {
  grantId: string
  axis: string // bandwidth | compute | money
  kind: string // plan_allowance | promotion | compensation | purchased
  amount: number
  remaining: number
  grantedAt: string
  expiresAt: string | null
}

// The three portal deep links the router accepts (router#1134). The button names the ACTION and
// the customer lands on it, instead of on the portal's home page with a menu.
export type PortalFlow = 'payment_method_update' | 'subscription_update' | 'subscription_cancel'

// How often the subscription is billed, from the subscription's Stripe price (router#1304).
// `null` when there is no subscription, when the summary has not landed, and on a router that
// predates the field. Every reader treats `null` as "unknown" and keeps its monthly behaviour.
export type BillingInterval = 'month' | 'year'

// What the router applies to an org that is lapsed or has an empty credit balance, resolved from
// its runtime flags (router#1306). The same for every org. The same three booleans the billing
// alert mails resolve, so a banner and a mail cannot disagree about one organization. The app
// writes its own sentences from them; the cause table is in `computeStop.ts`.
export type BillingConsequences = {
  refuseCreate: boolean // a lapsed or empty-balance org is refused new nodes, networks and services
  stopCompute: boolean // a lapsed or empty-balance org's provisioned machines are stopped
  slowBandwidth: boolean // served, not read: a stamped throttle is always a slowed org
}

export interface BillingState {
  plan: PlanRow | null // the active org's current plan
  plans: PlanRow[] // the catalog for the picker
  // The plan card's "then from $X/h" rate: the lowest non-zero price among OFFERED
  // `compute_classes` rows (`lowestComputeRate`, app#331). ⚠ NULL means the catalogue was not read,
  // and the card then prints no rate. It is never a typed fallback.
  computeFromCentsPerHour: number | null
  // subscription_status: active | past_due | unpaid | canceled | inactive.
  // ⚠⚠ THE OWNER'S ONLY, AND READ FROM THE ROUTER (app#376, router migration 0222): the column is
  // withheld from every app user, so it comes from the `billing` tool (`loadStatus`, `loadSummary`),
  // which is Owner only. Every other reader keeps 'inactive', which no banner arm matches: a Member
  // is not told about the org's payments, and still sees a throttle or a block, whose columns stay
  // readable (they are usage).
  status: string
  // The org whose `status` this is, or null when none was read. ⚠⚠ KEYED BY ORG, NOT A BOOLEAN, AND
  // NOT CLEARED WITH THE SUMMARY (review of app#387). Both pages call `clearSummary` on MOUNT, not
  // only on a switch: a status cleared there read 'inactive' until `loadSummary` landed, and stayed
  // 'inactive' when that Stripe read failed, so a subscribed Owner lost the lapse banner and was
  // offered Checkout. An org id, compared in `get`, also catches a switch that `setActiveOrg` does
  // not make (an org the reader left, read again by `useOrganizations.get`).
  statusOrgId: string | null
  currentPeriodEnd: string | null
  // ⚠ The Owner's only, from `credit` op=status (`loadCredit`), for the same reason as `status`.
  // Null for everyone else, and until the credit position is read.
  spendCapCents: number | null
  throttledUntil: string | null // organizations.throttled_until (2.11): future ⇒ nodes are throttled now
  // organizations.blocked_until (router#966): future ⇒ this org's customer data traffic is
  // REFUSED right now. ⚠⚠ IT IS A SECOND COLUMN AND NOT A WIDER THROTTLE. Throttling shapes
  // bytes to 1 Mbit/s; blocking stops them. Only a FREE org over its bandwidth allowance is ever
  // blocked — paid plans still throttle — so the two are never both the reason at once, and
  // reading either one as the other tells a customer something confidently wrong. Same
  // semantics as throttledUntil: live while it is in the future, and it lapses on its own if the
  // evaluator stops stamping, so null or past ⇒ not blocked.
  blockedUntil: string | null
  alerts: BillingAlert[] // current-period billing alerts for the banner (2.10)

  // ⚠⚠ `cancelAtPeriodEnd` IS WHAT TELLS "renews on" APART FROM "ends on". Stripe leaves a
  // cancelled subscription `active` until the period actually ends, so a page reading only
  // `status` tells a customer who has just cancelled that their plan renews — for up to a month,
  // about their own money (router#1133).
  cancelAtPeriodEnd: boolean
  // ⚠ BOTH ARE PART OF THE SUMMARY. Only `loadSummary` writes them, so they are cleared with
  // `cancelAtPeriodEnd` on every org switch, and a reader who cannot run that read (a Member)
  // keeps `null` — which every surface reads as "unknown", never as monthly or as "nothing is
  // enforced".
  interval: BillingInterval | null
  // `null` on a router that predates the field: every banner then keeps the wording it has today.
  consequences: BillingConsequences | null
  // Stripe's `customer.balance`, served unchanged as `customer_balance_cents` (router#1315).
  // ⚠⚠ STRIPE'S SIGN: NEGATIVE is credit OWED TO THE CUSTOMER, positive is what they owe. Only
  // `balanceLine` reads the sign. `null` when the router predates the field, or when what
  // arrived is not an integer — and null renders NOTHING, never "$0.00".
  customerBalanceCents: number | null
  card: BillingCard | null
  invoices: BillingInvoice[]
  hasMoreInvoices: boolean
  // ⚠ An org with no customer answers with an empty summary rather than an error, so `loaded`
  // means "we asked", not "there is something to show". `summaryError` is the third state and is
  // only ever surfaced to an org that HAS a subscription — see Billing.tsx.
  summaryLoaded: boolean
  summaryError: boolean

  // ⚠ `creditLoaded` means WE ASKED, not "there is something to show" — the summaryLoaded rule.
  // A router that predates the `credit` tool, a Member who is refused, and an arrears deployment
  // all leave `credit` null, and none of them is an error a customer should see.
  credit: CreditPosition | null
  creditLoaded: boolean
  // The live grants behind the balance (get_org_credit), for the breakdown app#146 asks for.
  //
  // ⚠ `useUsage` fetches the SAME rpc for the boost notice, and this is a second fetcher rather
  // than a shared one deliberately: the two stores have different lifecycles (billing is
  // app-wide and loaded at sign-in; usage is per-period and re-fetched on the picker), and
  // making one depend on the other would couple the banner's readiness to a page nobody may
  // have opened. One extra RPC on an Owner-only path is the cheaper mistake.
  creditGrants: CreditGrant[]
  // ⚠ NULL means "not asked" and [] means "this period holds none" — the same distinction the
  // router's payload draws, kept rather than collapsed so the list can render its own empty state.
  creditPurchases: CreditPurchase[] | null

  set: (state: Partial<BillingState>) => void
  get: () => Promise<void>
  subscribe: () => () => void
  upgrade: (plan: string, interval: 'monthly' | 'annual') => Promise<void>
  openPortal: (flow?: PortalFlow) => Promise<void>
  loadSummary: (startingAfter?: string) => Promise<void>
  loadStatus: () => Promise<void>
  clearSummary: () => void
  // The write halves and the read half, so `CreditBlock.onSave` can put both writes out before
  // either read (app#293). See the note above `writeSpendCap`.
  writeSpendCap: (capCents: number | null) => Promise<void>
  writeAutoReload: (triggerCents: number | null, reloadCents: number | null) => Promise<void>
  rereadRefillState: () => Promise<void>
  rereadAfterRefillWrite: () => void
  loadCredit: () => Promise<void>
  buyCredit: (amountCents: number) => Promise<void>
  setAutoReload: (triggerCents: number | null, reloadCents: number | null) => Promise<void>
  setCreditSpending: (enabled: boolean) => Promise<void>
  reset: () => void
}

const state = {
  plan: null as PlanRow | null,
  plans: [] as PlanRow[],
  computeFromCentsPerHour: null as number | null,
  status: 'inactive',
  statusOrgId: null as string | null,
  currentPeriodEnd: null as string | null,
  spendCapCents: null as number | null,
  throttledUntil: null as string | null,
  blockedUntil: null as string | null,
  alerts: [] as BillingAlert[],
  cancelAtPeriodEnd: false,
  interval: null as BillingInterval | null,
  consequences: null as BillingConsequences | null,
  customerBalanceCents: null as number | null,
  card: null as BillingCard | null,
  invoices: [] as BillingInvoice[],
  hasMoreInvoices: false,
  summaryLoaded: false,
  summaryError: false,
  credit: null as CreditPosition | null,
  creditLoaded: false,
  creditGrants: [] as CreditGrant[],
  creditPurchases: null as CreditPurchase[] | null,
}

// Billing state is app-wide (the 2.10 lapse/dunning banner reads `status` everywhere), so it's a
// controller-registered store — loaded at sign-in, re-loaded on active-org switch (see
// useOrganizations.setActiveOrg), kept live by the realtime subscription below. Reads are Supabase
// (RLS-scoped); the Owner-gated mutations (upgrade/portal/spend-cap) route through callTool.
/**
 * Whether a response asked for on behalf of `orgId` may still be written to the store (app#224).
 *
 * ⚠⚠ AN ORG-SCOPED READ THAT LANDS LATE WRITES ANOTHER ORGANIZATION'S MONEY UNDER THIS ONE'S NAME.
 * `Billing.tsx` clears the summary on an org change and fires a new read; the OLD read is still in
 * flight, nothing in the response identifies which organization it answered for, and whichever
 * lands last wins. Switching A → B quickly then shows A's payment method and A's invoices under
 * B's name — and sets `summaryLoaded: true`, so they are presented as a landed read rather than a
 * loading state. app#216's three-state `summaryVisibility` cannot help: `shown` is exactly the
 * state that permits a populated answer to be believed.
 *
 * ⚠ `cancelAtPeriodEnd` makes it more than a mixup, because the period verb and the Cancel control
 * derive from it: B could read "ends" and offer "Keep my plan" on A's answer.
 *
 * ⚠⚠ IT GUARDS THE CATCH BRANCHES TOO, and that is not symmetry for its own sake. A stale FAILURE
 * writes `summaryError: true` against the new organization, which renders an error about a read
 * that succeeded for the org actually on screen.
 *
 * ⚠ A generation counter would also work and was the issue's suggestion. The active org is the
 * same test with no second piece of state to keep in step — the store already reads it at the top
 * of every action, so the comparison is against the one value that decides what the panel shows.
 */
const stillActive = (orgId: string): boolean => useOrganizations.getState().activeOrgId === orgId

/**
 * Read the credit position again once the router has had time to buy the refill a settings write
 * permitted (nobgp-router#1328).
 *
 * ⚠⚠ IT IS NOT AWAITED, AND THAT IS THE WHOLE SHAPE. Save must return when the write returns; a
 * six-second await would leave the button spinning over a purchase that is not the customer's to
 * wait for, and a failed refill would then look like a failed save.
 *
 * ⚠⚠ IT PUTS BACK WHAT A FAILED READ TOOK OFF THE SCREEN, AND WITHOUT THAT THIS IS A REGRESSION.
 * `loadCredit` turns its OWN failure into `credit: null`, `creditGrants: []`, `creditPurchases:
 * null` — the right answer when it is the only thing that ran, because `CreditBlock` returns null
 * on a null position and the block simply does not render. Reached from a BACKGROUND TIMER it is
 * the wrong answer twice: the whole credit panel vanishes six seconds after a save that WORKED,
 * with no error and nothing the customer did, and it does not come back — `BillingBanner` re-reads
 * only `if (!creditLoaded)`, which that same catch has just set true, so on `/billing` it is gone
 * until a reload.
 *
 * ⚠ So the position is held across the read and restored if the read blanked it. Unlike
 * `buyCredit`'s guard below — which restores the position ALONE, because there its reply is a
 * newer fact and only the grants are missing — this restores ALL THREE, because a failed read
 * carries no newer fact at all: the screen goes back to exactly what it showed, which is the truth
 * as last known.
 *
 * ⚠ A no-op when the read SUCCEEDS, including when it legitimately answers `credit: null` — the
 * restore asks whether we HELD a position and lost it, never whether one is absent.
 *
 * ⚠ The organization is checked at FIRE time, not at schedule time: six seconds is long enough to
 * switch orgs, and re-reading then would write another organization's position over the one on
 * screen. `loadCredit` re-reads the active org itself, so the guard is about not firing at all.
 *
 * ⚠⚠ AND IT IS CHECKED AGAIN AFTER THE READ, which is not belt-and-braces. An org switch DURING
 * those three calls makes `loadCredit` return without setting anything, so the store then holds
 * the NEW org's position — and an unguarded restore would write the old org's balance over it.
 */
const rereadAfterRefill = (orgId: string, get: () => BillingState): void => {
  setTimeout(() => {
    if (!stillActive(orgId)) return
    const held = {
      credit: get().credit,
      creditGrants: get().creditGrants,
      creditPurchases: get().creditPurchases,
    }
    void get()
      .loadCredit()
      .then(() => {
        if (!stillActive(orgId)) return
        if (held.credit && !get().credit) get().set(held)
      })
      .catch(() => {})
  }, REFILL_REREAD_MS)
}

const useBilling = create<BillingState>()((set, get) => ({
  ...state,
  set,
  get: async () => {
    const orgId = useOrganizations.getState().activeOrgId
    if (!orgId) return
    // ⚠ Another org's `status` must not stand for this one while the reads below run: on a switch
    // it would put org A's lapse banner over org B. Dropped at once, read again below.
    if (get().statusOrgId !== orgId) set({ status: 'inactive', statusOrgId: null })
    try {
      const [orgRes, plansRes, alertsRes, classesRes] = await Promise.all([
        // ⚠⚠ NO `subscription_status` AND NO `spend_cap_cents` (app#376): router migration 0222
        // withholds both, and a select that names one fails AS A WHOLE, which would take the plan,
        // the throttle and the block with it. See `utilities/moneyReads.ts`.
        supabase.from('organizations').select(ORG_BILLING_COLUMNS).eq('id', orgId).single(),
        supabase.from('plans').select('*'),
        supabase.from('billing_alerts').select('kind, axis, period').eq('org_id', orgId).eq('period', currentPeriod()),
        // Readable by any authenticated user (`compute_classes_read` is `USING (true)`). Not
        // filtered on `offered` here: `lowestComputeRate` does that, where a test can reach it.
        supabase.from('compute_classes').select('offered, price_cents_per_hour'),
      ])
      // ⚠⚠ THE SAME GUARD, ON THE READ THAT DRIVES THE MOST SURFACES — see `stillActive`. `orgRes`
      // and `alertsRes` are both `.eq(…, orgId)`, and `setActiveOrg` fires this on every switch,
      // so two quick switches can leave A's `status`, `plan`, `spendCapCents`, `throttledUntil`
      // and `alerts` standing under B's name — and `status` is what `BillingBanner` reads on EVERY
      // page, so the wrong answer follows the customer off the billing screen.
      if (!stillActive(orgId)) return
      const plans = (plansRes.data ?? []).map(r => camelcaseKeys(r)) as PlanRow[]
      const org = orgRes.data
      set({
        plans,
        computeFromCentsPerHour: lowestComputeRate((classesRes.data ?? []).map(r => camelcaseKeys(r))),
        plan: plans.find(p => p.id === org?.plan_id) ?? null,
        currentPeriodEnd: org?.current_period_end ?? null,
        throttledUntil: org?.throttled_until ?? null,
        blockedUntil: org?.blocked_until ?? null,
        alerts: (alertsRes.data ?? []).map(r => camelcaseKeys(r)) as BillingAlert[],
      })
      // ⚠ The Owner's `status` comes from the router, ONCE per org (app#376). Not on every call:
      // this runs on every `organizations` realtime event, and the throttle stamp is renewed on a
      // timer, so a `billing` read here would be a Stripe round trip per renewal. The Billing and
      // Usage pages read the summary, which refreshes it; another org (`statusOrgId`) reads it again.
      if (!readsMoney(useOrganizations.getState().role)) {
        set({ status: 'inactive', statusOrgId: null, spendCapCents: null })
      } else if (get().statusOrgId !== orgId) {
        void get().loadStatus()
      }
    } catch (error) {
      console.warn(error)
    }
  },
  // Unfiltered on `organizations` + `billing_alerts` (not a specific row/org): the active org can
  // change without a re-subscribe (the controller only re-subscribes on refresh), and both are
  // RLS-scoped so we only ever receive our own orgs' rows. The organizations stream flips the
  // badge from the 2.5 webhook a beat after a Checkout redirect; the billing_alerts INSERT stream
  // lights up the 2.10 banner the instant an advisory alert is written (needs billing_alerts in
  // the supabase_realtime publication — router migration 0010). The organizations stream re-reads
  // the billing row alone; the alerts stream re-reads the credit position with it, for the reason
  // its own note gives.
  subscribe: () => {
    const subscription = supabase
      .channel('organizations-billing')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'organizations' }, () => {
        void get().get()
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'billing_alerts' }, () => {
        // ⚠⚠ THE CREDIT POSITION IS RE-READ TOO, AND SINCE app#291 IT HAS TO BE. `get()` refreshes
        // `alerts` and `spend_cap_cents` and NOT `credit` — and the `cap_*` banners now suppress on
        // a MEASURED false from `credit.autoRefilledThisPeriodCents`. Refreshing only half of that
        // comparison hands the freshly written row to a refill figure from before the refill that
        // tripped it, so `capAlertStillReached` answers false and the banner this stream exists to
        // light up never renders. An Owner sitting on Billing with $4 refilled under a $50 cap
        // watches the refills reach $50, the sweep writes `cap_100`, and the page says nothing.
        //
        // ⚠ `credit` is null for a Member and stays null — `loadCredit` swallows the refusal — and
        // null is "cannot see", which KEEPS the banner. So this costs a Member one refused call and
        // changes nothing they see.
        void get().rereadRefillState()
      })
      .subscribe()
    return () => {
      supabase.removeChannel(subscription)
    }
  },
  upgrade: async (plan, interval) => {
    const orgId = useOrganizations.getState().activeOrgId
    if (!orgId) return
    const res = await callTool<{ checkout_url: string }>('create_checkout', { org_id: orgId, plan, interval })
    window.location.href = res.checkout_url
  },
  openPortal: async flow => {
    const orgId = useOrganizations.getState().activeOrgId
    if (!orgId) return
    // ⚠ `flow` is omitted, never sent empty: an unknown flow is refused by name, and the router
    // reads an absent one as "the portal's home page", which is what every caller wanted before
    // the deep links existed.
    const res = await callTool<{ portal_url: string }>('create_billing_portal', {
      org_id: orgId,
      ...(flow ? { flow } : {}),
    })
    window.location.href = res.portal_url
  },

  // Forget the previous organization's summary, BEFORE anything is fetched for the next one. The
  // store is app-wide and `get()` does not touch these fields, so without this the card and the
  // invoices of the org the customer just left stay on screen — and for an org they are NOT the
  // Owner of, `loadSummary` never runs, so the previous org's money is what they keep reading.
  //
  // ⚠ ONE ACTION, TWO CALLERS (app#268). `Billing.tsx` held this `set({…})` inline, and `Usage.tsx`
  // now reads the summary too (the credit block on it names the payment method), so the list of
  // fields lives here once: a summary field added to `loadSummary` and forgotten here goes stale
  // on both pages, not on one of them.
  //
  // ⚠ `cancelAtPeriodEnd` IS PART OF THE SUMMARY and was once left behind. Only `loadSummary`
  // writes it, so without this the previous org's answer decides this org's verb — and for a
  // reader who cannot run that read it decides it for good.
  clearSummary: () =>
    set({
      card: null,
      invoices: [],
      hasMoreInvoices: false,
      cancelAtPeriodEnd: false,
      // Written only by `loadSummary`, like `cancelAtPeriodEnd` (app#264, app#270).
      interval: null,
      consequences: null,
      customerBalanceCents: null,
      summaryLoaded: false,
      summaryError: false,
      // ⚠ NOT `status`: it is keyed by org (`statusOrgId`), and this runs on every page mount.
    }),

  // The Stripe half of the billing page: the card, the invoices, and whether the subscription is
  // set to end. Owner-only on the router, so a member never calls it.
  //
  // ⚠ Paging APPENDS. `startingAfter` is Stripe's own cursor — the id of the last row on the page
  // just read — because an offset over a list that grows at the top repeats a row every time an
  // invoice is issued mid-read.
  loadSummary: async startingAfter => {
    const orgId = useOrganizations.getState().activeOrgId
    if (!orgId) return
    try {
      const res = await callTool<{
        cancel_at_period_end: boolean
        // ⚠ Both OPTIONAL on the type: a router that predates router#1304 / router#1306 serves
        // neither, and the read below must not turn that into a wrong claim.
        interval?: string
        consequences?: { refuse_create: boolean; stop_compute: boolean; slow_bandwidth: boolean }
        // ⚠ OPTIONAL for the same reason: absent before the router#1315 release, and absence
        // must render nothing.
        customer_balance_cents?: number
        payment_method: {
          type: string
          brand: string
          last4: string
          exp_month: number
          exp_year: number
          account: string
        } | null
        invoices: Array<Record<string, unknown>>
        has_more_invoices: boolean
        subscription_status?: string
      }>('billing', { org_id: orgId, ...(startingAfter ? { invoice_starting_after: startingAfter } : {}) })

      // ⚠ The org may have changed while this was in flight — see `stillActive`.
      if (!stillActive(orgId)) return
      const page = (res.invoices ?? []).map(r => camelcaseKeys(r)) as BillingInvoice[]
      const status = subscriptionStatusFrom(res)
      set({
        ...(status ? { status, statusOrgId: orgId } : {}),
        cancelAtPeriodEnd: !!res.cancel_at_period_end,
        // ⚠ Only the two spellings the router documents. `""` (no subscription), an absent field
        // and anything else all read as unknown, so an older router changes nothing on screen.
        interval: res.interval === 'month' || res.interval === 'year' ? res.interval : null,
        consequences: res.consequences
          ? {
              refuseCreate: !!res.consequences.refuse_create,
              stopCompute: !!res.consequences.stop_compute,
              slowBandwidth: !!res.consequences.slow_bandwidth,
            }
          : null,
        // ⚠ `Number.isInteger`, not a truthiness check: 0 is a valid answer (nothing owed either
        // way) and is stored as 0, which `balanceLine` prints nothing for. A string, a float or
        // an absent field all read as "unknown", and unknown says nothing on screen.
        customerBalanceCents: Number.isInteger(res.customer_balance_cents) ? (res.customer_balance_cents as number) : null,
        card: res.payment_method ? (camelcaseKeys(res.payment_method) as BillingCard) : null,
        invoices: startingAfter ? [...get().invoices, ...page] : page,
        hasMoreInvoices: !!res.has_more_invoices,
        summaryLoaded: true,
        summaryError: false,
      })
    } catch (error) {
      // ⚠ A router that predates the `billing` tool answers 404 here, and that is the state during
      // the deploy window — router first, then the app. It must not put an error in front of a
      // customer, so the sections simply do not render; `summaryError` is shown only where the
      // absence would otherwise be confusing (an org that HAS a subscription).
      console.warn(error)
      // ⚠ A stale FAILURE is as wrong as a stale success: it would render an error about a read
      // that succeeded for the organization actually on screen.
      if (!stillActive(orgId)) return
      set({ summaryLoaded: true, summaryError: true })
    }
  },
  // The Owner's `subscription_status`, alone (app#376). The `billing` tool is the Owner's one door to
  // it since router migration 0222; one invoice is asked for, because the invoices are not what
  // this read is for. ⚠ A failure keeps what was there and says nothing: a router that answers 404
  // or a Stripe blip must not put a lapse banner up or take one down.
  loadStatus: async () => {
    const orgId = useOrganizations.getState().activeOrgId
    if (!orgId || !readsMoney(useOrganizations.getState().role)) return
    try {
      const res = await callTool('billing', { org_id: orgId, invoice_limit: 1 })
      if (!stillActive(orgId)) return
      const status = subscriptionStatusFrom(res)
      if (status) set({ status, statusOrgId: orgId })
    } catch (error) {
      console.warn('billing status unavailable', error)
    }
  },
  // ⚠⚠ THE WRITE HALVES ARE SEPARATE SO ONE SAVE CAN ORDER THEM (app#293). A composed action that
  // writes and then re-reads is right for a caller that makes ONE change — and wrong for the
  // panel's Save, which makes two: the reads then sit between the two writes, inside the router's
  // ~2 s settle. `writeSpendCap` and `writeAutoReload` are the calls with no reads;
  // `rereadRefillState` is the reads with no call.
  //
  // ⚠ `setAutoReload` below KEPT its composed form because it kept a caller — `onToggleRefill`,
  // which switches the rule off and wants the read that follows. `setSpendCap` had the same shape
  // and lost its last caller to this change, so it is gone rather than left standing with a
  // comment claiming callers it does not have: a second way to write the cap, one of them carrying
  // the read order this issue exists to remove, is how the two come back apart.
  writeSpendCap: async capCents => {
    const orgId = useOrganizations.getState().activeOrgId
    if (!orgId) return
    await callTool('set_spend_cap', { org_id: orgId, cap_cents: capCents })
  },

  writeAutoReload: async (triggerCents, reloadCents) => {
    const orgId = useOrganizations.getState().activeOrgId
    if (!orgId) return
    const res = await callTool('credit', {
      org_id: orgId,
      op: 'auto_reload',
      trigger_cents: triggerCents,
      reload_cents: reloadCents,
    })
    set({ credit: creditPositionFrom(res) })
  },

  // ⚠ The two reads run TOGETHER: they write disjoint parts of the store and neither reads what
  // the other sets, so there is no order between them to keep (app PR #292).
  rereadRefillState: async () => {
    await Promise.all([get().get(), get().loadCredit()])
  },

  // ⚠ The LATE read, for the purchase the router makes out of band after a settings write
  // (nobgp-router#1328). The immediate read above is too early to see it.
  rereadAfterRefillWrite: () => {
    const orgId = useOrganizations.getState().activeOrgId
    if (!orgId) return
    rereadAfterRefill(orgId, get)
  },

  // Owner-only on the router (ActionManageBilling), like `billing` and `set_spend_cap`.
  //
  // ⚠ A failure is SWALLOWED into `credit: null`, never surfaced. Three ordinary states reach it
  // and none is worth an error in front of a customer: a Member is refused, a router that
  // predates the tool answers 404, and an org with no plan row resolves to nothing. The block
  // simply does not render. `creditLoaded` says we asked.
  loadCredit: async () => {
    // ⚠⚠ `callTool` HAS ALREADY UNWRAPPED THE PAYLOAD, AND THIS TOOL'S PAYLOAD KEY IS ITS OWN
    // NAME. The body is `{call_id, op, done, duration_ms, credit:{…}}` and `unwrapToolPayload`
    // returns `body.credit`, so `res` here IS the CreditPayload — `res.credit` would be
    // `undefined` on every call, `credit` would be null forever, and the whole surface would
    // render nothing on every deployment including one where `credit_billed` is true. That is
    // app#173 exactly, one tool later, and it is silent: the arrears deployment we ship against
    // renders nothing for the RIGHT reason, so the defect could not be seen from prod.
    const orgId = useOrganizations.getState().activeOrgId
    if (!orgId) return
    try {
      // ⚠⚠ `purchases` IS ITS OWN CALL AND ITS OWN FAILURE (nobgp-router#1209). `op=purchases`
      // returns everything `op=status` returns PLUS the list, so one call could serve both — and
      // must not. A router that predates the op answers an error, and folding the two would take
      // the whole credit block down with it during the deploy window. The list is the part that
      // may be missing; the position is not.
      // ⚠ Owner only, like the tool (app#376): since router migration 0222 `get_org_credit` answers
      // 42501 to an Admin or a Member, and nothing here is theirs to read.
      if (!readsMoney(useOrganizations.getState().role)) {
        set({ credit: null, creditGrants: [], creditPurchases: null, creditLoaded: true, spendCapCents: null })
        return
      }
      const [res, grantsRes, purchasesRes] = await Promise.all([
        callTool('credit', { org_id: orgId, op: 'status' }),
        // ⚠ NOT period-scoped, because the function is not: it answers "what does this org hold
        // NOW". The block only ever renders the current position, so there is no past month for
        // it to be wrong about.
        supabase.rpc('get_org_credit', { p_org_id: orgId }),
        // ⚠ Caught HERE rather than by the try below, so an older router costs the list and
        // nothing else. `null` is what the block renders as "no history to show".
        callTool('credit', { org_id: orgId, op: 'purchases' }).catch(error => {
          console.warn('credit purchases unavailable', error)
          return null
        }),
      ])
      if (!stillActive(orgId)) return
      const position = creditPositionFrom(res)
      set({
        credit: position,
        // ⚠ The spend cap is read HERE since app#376: `organizations.spend_cap_cents` is withheld
        // by router migration 0222, and `credit` op=status is the Owner's door to it.
        spendCapCents: position?.spendCapCents ?? null,
        creditGrants: (grantsRes.data ?? []).map(r => camelcaseKeys(r)) as CreditGrant[],
        creditPurchases: creditPurchasesFrom(purchasesRes),
        creditLoaded: true,
      })
    } catch (error) {
      console.warn(error)
      if (!stillActive(orgId)) return
      set({ credit: null, creditGrants: [], creditPurchases: null, creditLoaded: true, spendCapCents: null })
    }
  },

  // ⚠⚠ EVERY MUTATION SETS STATE FROM ITS OWN REPLY AND NEVER RE-READS. The router builds the
  // payload AFTER the mutation, deliberately — "a reply assembled from the request says what was
  // asked for; this says what is now true" — so a follow-up read would be a second round trip
  // that can only ever be staler. It also means a failed call leaves the previous position
  // standing, which is correct: nothing changed.
  //
  // ⚠ These do NOT catch. The caller is a control the customer is looking at, and a purchase
  // that failed must say so — unlike the load above, whose silence is the right answer.
  //
  // ⚠⚠ `buyCredit` IS THE ONE EXCEPTION, AND IT IS app#221. The rule above holds for the POSITION,
  // which the reply does carry. It cannot hold for the GRANTS: the `credit` payload has no grants
  // array at all — the breakdown comes from the `get_org_credit` RPC — so a buy that set only the
  // reply left the balance right and the rows beneath it a purchase behind, and the rows stopped
  // adding up to the total at the one moment the customer is checking that their money arrived.
  // That is app#135's rule broken on the screen it was written for.
  buyCredit: async amountCents => {
    const orgId = useOrganizations.getState().activeOrgId
    if (!orgId) return
    // ⚠⚠ THE ATTEMPT IS A FACT WHATEVER THE OUTCOME (app#257). `callTool` THROWS on a decline, so
    // the reload below — which is what fetches the purchase list — was reached only when the
    // purchase succeeded. The list therefore refreshed on exactly the outcome that needed it less,
    // and a customer who had just been told "your card was declined" saw a list that did not
    // mention the attempt until they reloaded the page.
    //
    // ⚠ The alert is TRANSIENT — dismissible, and gone on the next navigation or org switch — so
    // for the rest of that session the durable record disagreed with what the customer was told.
    //
    // ⚠⚠ THE RE-THROW IS LOAD-BEARING. `run()` renders the alert from this error, and swallowing
    // it to reach the reload would trade a stale list for a silent failure, which is strictly
    // worse: the customer would be told nothing at all. The reload is best-effort and must not be
    // able to replace the card's own error.
    let res: unknown
    try {
      res = await callTool('credit', {
        org_id: orgId,
        op: 'buy',
        amount_cents: amountCents,
      })
    } catch (error) {
      // ⚠⚠ THE RELOAD MUST NOT BE ABLE TO TAKE THE ERROR OFF THE SCREEN. `loadCredit` swallows
      // every failure into `credit: null`, `showsCredit` is false on a null position, and the
      // alert `run()` is about to set lives INSIDE `CreditBlock` — so a failed reload here
      // unmounts the component that was going to report the failure. The two failures are
      // correlated, not independent: a transport fault fails the buy and then fails the reload
      // for the same reason, so the customer would press Buy, watch the whole credit block
      // disappear, and be told nothing at all. That is the silent failure the re-throw below
      // exists to prevent, arriving by the other door.
      //
      // ⚠ The standing position is the right thing to put back. Nothing was charged, so the
      // position from before the attempt is still true — this is the failure-path twin of the
      // `if (!get().credit) set({ credit: bought })` that guards the success path.
      const standing = get().credit
      await get().loadCredit().catch(() => {})
      if (!get().credit) set({ credit: standing })
      throw error
    }
    // ⚠ The position lands FIRST, from the reply, so the balance moves as soon as the purchase
    // does. `loadCredit` then reconciles it and brings the grants with it. Reversing these two
    // would make the customer wait two more round trips to see their own money.
    const bought = creditPositionFrom(res)
    set({ credit: bought })
    await get().loadCredit()
    // ⚠⚠ THE RECONCILE MUST NOT BE ABLE TO UNDO THE PURCHASE ON SCREEN. `loadCredit` swallows
    // every failure into `credit: null`, which is the right answer when it is the only thing that
    // ran — `CreditBlock` returns null on a null position and the block simply does not render.
    // Reached from HERE that answer is wrong twice over: the card has already been charged, and a
    // transient read — a 404 in the router-first deploy window, a `get_org_credit` blip — would
    // take the balance, the auto-refill rule and the Buy control off the screen with no error
    // shown, at the one moment the customer is checking that their money arrived. That is a worse
    // failure than the stale breakdown this re-read exists to fix. The reply is the newer fact, so
    // it stands; only the grants are missing, and `byKind.length > 0` renders no rows rather than
    // rows that do not sum.
    if (!get().credit) set({ credit: bought })
  },

  // ⚠ BOTH or NEITHER. Sending one alone is `invalid_args` — a trigger with no amount is a rule
  // that can never act, and an amount with no trigger one that never fires. Nulls switch it off.
  setAutoReload: async (triggerCents, reloadCents) => {
    const orgId = useOrganizations.getState().activeOrgId
    if (!orgId) return
    const res = await callTool('credit', {
      org_id: orgId,
      op: 'auto_reload',
      trigger_cents: triggerCents,
      reload_cents: reloadCents,
    })
    set({ credit: creditPositionFrom(res) })
    // ⚠ The other half of the pair above: arming the rule permits a refill too, and the router
    // attempts it out of band. The reply this just set is the position from BEFORE that decision.
    if (rulePermitsRefill(triggerCents, reloadCents)) rereadAfterRefill(orgId, get)
  },

  // ⚠ `enabled` is REQUIRED and is sent explicitly. The router refuses an absent one rather than
  // defaulting it, because defaulting either way makes a malformed call silently change how a
  // customer pays.
  setCreditSpending: async enabled => {
    const orgId = useOrganizations.getState().activeOrgId
    if (!orgId) return
    const res = await callTool('credit', {
      org_id: orgId,
      op: 'spending',
      enabled,
    })
    set({ credit: creditPositionFrom(res) })
  },

  reset: () => set({ ...state }),
}))

export default useBilling
